import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NavigateHook } from '@wfp-common/hooks/useNavigate';
import { withNavigate } from '@wfp-common/hooks/withNavigate';

import { ManagerPermission } from '../permission-profiles/permission';
import { AsyncTask, AsyncTasksTypes, mapAsyncTaskTypeToRequiredPostingAccessFeature } from '../utils/asyncTasks';
import { getNextAction } from '../utils/AsyncTaskDetailsView';
import { useAuth } from './hooks/useAuth';
import { useAsyncTasks } from './hooks/useAsyncTasks';
import { useMobileDevicesUpload } from './hooks/useMobileDevicesUpload';
import DryRunDialog from './components/DryRunDialog';
import { DownloadStaticFileButton } from '../utils/DownloadStaticFileButton';

/**
 * Interface extending AsyncTask with specific fields needed for mobile device uploads
 */
interface MobileDeviceUploadTask extends AsyncTask {
    resultFileUrl?: string;
    id: string;
    fileName: string;
}

interface MobileDeviceUploadListProps {
    data: Array<MobileDeviceUploadTask>;
    canAuthorizeTask?: boolean;
    canCancelTask?: boolean;
    authorizedManagerId: string;
    authorize: (id: string) => Promise<void>;
    cancel: (id: string) => Promise<void>;
    downloadFile: (id: string) => Promise<void> | void;
    canDownload: boolean;
    navigate: NavigateHook;
}

const MobileDeviceUploadList: React.FC<MobileDeviceUploadListProps> = (props) => {
    const { data, authorize, cancel, downloadFile, canDownload, navigate } = props;
    const { auth } = useAuth();

    if (!data || data.length === 0) {
        return (
            <div
                style={{
                    padding: '20px',
                    textAlign: 'center',
                    border: '1px dashed #ddd',
                    borderRadius: '4px',
                    margin: '20px 0',
                }}
            >
                <div style={{ color: '#666', marginBottom: '10px', fontSize: '16px' }}>
                    <i className="fa fa-info-circle" style={{ marginRight: '5px' }} />
                    No upload tasks found.
                </div>
                <p style={{ color: '#888', fontSize: '14px', margin: '0' }}>
                    Upload a file to start the import process.
                </p>
            </div>
        );
    }

    return (
        <div style={{ margin: '20px 0' }}>
            <table className="wfp-table">
                <thead>
                    <tr>
                        <th>File Name</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((task) => (
                        <tr
                            className="cursor-pointer"
                            key={task.id}
                            onClick={() => navigate(`/tasks/${task.type}/${task.id}`)}
                        >
                            <td>{task.fileName}</td>
                            <td>{task.status}</td>
                            <td>{new Date(task.createdAt).toLocaleString()}</td>
                            <td>
                                {canDownload && task.resultFileUrl && (
                                    <button
                                        className="wfp-btn wfp-btn--primary wfp-btn--sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            downloadFile(task.id);
                                        }}
                                        type="button"
                                    >
                                        Download
                                    </button>
                                )}
                                {(() => {
                                    const permission = mapAsyncTaskTypeToRequiredPostingAccessFeature(task.type);
                                    const actions = getNextAction(task, auth, permission);
                                    const handleClick = async (e: React.MouseEvent, text: string) => {
                                        e.stopPropagation();
                                        if (text === 'Post') {
                                            await authorize(task.id);
                                        } else if (text === 'Cancel' || text === 'Reject') {
                                            await cancel(task.id);
                                        }
                                    };
                                    return (
                                        <span>
                                            {actions.map((action) => (
                                                <a
                                                    className={'ml-0 mr-5'}
                                                    key={`${task.id}-${action.text}`}
                                                    onClick={(e) => handleClick(e, action.text)}
                                                    style={action.style}
                                                >
                                                    {action.text}
                                                </a>
                                            ))}
                                        </span>
                                    );
                                })()}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const MobileDeviceUploadListWithNavigate = withNavigate(MobileDeviceUploadList, 'MobileDeviceUploadList');

const MobileDevicesUploadPage: React.FC = () => {
    // Use custom hooks for authentication, async tasks, and file upload
    const { hasFeatureAccess, getManagerId } = useAuth();
    const [taskStateError, setTaskStateError] = useState<string | null>(null);
    const { tasks, loadAsyncTasksList, authorize, downloadFile, cancel } = useAsyncTasks(
        AsyncTasksTypes.MOBILE_DEVICES_IMPORT
    );
    const { uploading, uploadFile } = useMobileDevicesUpload(setTaskStateError);

    // Local state
    const [file, setFile] = useState<File | null>(null);
    const [showDryRun, setShowDryRun] = useState<boolean>(false);
    const [asyncTask, setAsyncTask] = useState<AsyncTask | null>(null);
    const defaultPageSize = 25;

    // Check permissions
    const canPark = hasFeatureAccess(ManagerPermission.mobileDevicesCreateUpdatePark);
    const canPost = hasFeatureAccess(ManagerPermission.mobileDevicesCreateUpdatePost);
    const canCancel = hasFeatureAccess(ManagerPermission.mobileDevicesCreateUpdatePark);
    const canDownload = hasFeatureAccess(ManagerPermission.mobileDevicesCreateUpdatePark);
    const authorizedManagerId = getManagerId();

    // Load tasks on component mount
    useEffect(() => {
        loadAsyncTasksList(1, defaultPageSize);
    }, [loadAsyncTasksList]);

    // Handle file upload
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            event.target.value = '';

            try {
                // Start dry run
                setShowDryRun(true);
                const dryRunResult = await uploadFile(selectedFile, true);

                setAsyncTask(dryRunResult);
            } catch (err) {}
        }
    };

    // Handle dry run confirmation
    const handleDryRunConfirm = async (dryRunAsyncTaskId: string) => {
        try {
            await uploadFile(file, false, dryRunAsyncTaskId);
            await loadAsyncTasksList(1, defaultPageSize);
            setShowDryRun(false);
            setFile(null);
        } catch (err) {
            const parseResponseError = async (res: Response): Promise<string> => {
                try {
                    const contentType = res.headers.get('content-type') || '';
                    if (contentType.includes('application/json')) {
                        const data = await res.json();
                        if (typeof data?.message === 'string') return data.message as string;
                        if (Array.isArray(data?.message)) return (data.message as string[]).join(', ');
                        if (typeof data?.error === 'string') return data.error as string;
                        return JSON.stringify(data);
                    } else {
                        const text = await res.text();
                        return text || `Request failed with status ${res.status}`;
                    }
                } catch {
                    return `Request failed with status ${res.status}`;
                }
            };
            let errMessage = 'Error confirming dry run';
            if (err instanceof Response) {
                errMessage = await parseResponseError(err);
            } else if (err && typeof (err as { message?: unknown }).message === 'string') {
                errMessage = (err as { message: string }).message;
            }
            setTaskStateError(errMessage);
        }
    };

    // Handle dry run cancellation
    const handleDryRunCancel = () => {
        setShowDryRun(false);
        setFile(null);
        setAsyncTask(null);
        setTaskStateError(null);
    };

    return (
        <main>
            <nav className="wfp-breadcrumbs">
                <ol className="breadcrumbs--wrapper">
                    <li className="breadcrumbs--item">
                        <Link className="breadcrumbs--link" to="/home">
                            <span>Home</span>
                        </Link>
                    </li>
                    <li className="breadcrumbs--item">
                        <Link className="breadcrumbs--link" to="/mobile-devices">
                            <span>Mobile Devices</span>
                        </Link>
                    </li>
                    <li className="breadcrumbs--item">
                        <span className="breadcrumbs--last">Import</span>
                    </li>
                </ol>
            </nav>

            <h3>Mobile Devices Import</h3>

            {canPark && (
                <div>
                    <p>Import the Mobile Devices file by clicking the button below.</p>
                    <p>
                        Please ensure that you have exported this list with the correct structure.{' '}
                        <DownloadStaticFileButton filename={'MobileDevicesUploadExample.xlsx'}>
                            See example import file
                        </DownloadStaticFileButton>
                    </p>
                    <div className="wfp-form--actions">
                        <button className="wfp-btn replaced-input-file" disabled={uploading} type="button">
                            <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                            <input
                                accept=".xlsx,.xls,.csv"
                                disabled={uploading}
                                name="data"
                                onChange={handleFileChange}
                                type="file"
                            />
                        </button>
                    </div>
                </div>
            )}

            {showDryRun && (
                <DryRunDialog
                    asyncTask={asyncTask}
                    downloadFile={downloadFile}
                    onCancel={handleDryRunCancel}
                    onConfirm={handleDryRunConfirm}
                    taskStateError={taskStateError}
                    title="Mobile Devices Import"
                />
            )}

            {tasks && (
                <div>
                    <MobileDeviceUploadListWithNavigate
                        authorize={authorize}
                        authorizedManagerId={authorizedManagerId}
                        canAuthorizeTask={canPost}
                        canCancelTask={canCancel}
                        canDownload={canDownload}
                        cancel={cancel}
                        data={tasks.items as MobileDeviceUploadTask[]}
                        downloadFile={downloadFile}
                    />
                </div>
            )}
        </main>
    );
};

export default MobileDevicesUploadPage;
